// src/modules/app-views/services/pages/Init/modals/DataImportModal/StepProcessing.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect } from 'react';
import { Progress, Row, Col, Statistic, Result, Flex, Typography } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

const { Title } = Typography;

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const StepProcessingContent = ({ progress, importStatus, progressPercent, startExecution, hasStartedExecutionRef }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Start execution when entering this step (runs once via ref guard)
  useEffect(() => {
    if (hasStartedExecutionRef.current) return;
    hasStartedExecutionRef.current = true;
    startExecution();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (importStatus === 'completed') {
    return (
      <Result
        status="success"
        title={translateX('data_import_completed')}
        subTitle={`${progress?.imported || 0} registros importados com sucesso`}
        extra={
          <Row gutter={[16, 16]} style={{ maxWidth: 400, margin: '0 auto' }}>
            <Col span={12}>
              <Statistic
                title={translateX('data_import_imported_records')}
                value={progress?.imported || 0}
                styles={{ content: { color: '#52c41a' } }}
                prefix={<CheckCircleOutlined />}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title={translateX('data_import_updated_records')}
                value={progress?.updated || 0}
                styles={{ content: { color: '#1677ff' } }}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title={translateX('data_import_skipped_records')}
                value={progress?.skipped || 0}
                styles={{ content: { color: '#faad14' } }}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title={translateX('data_import_failed_records')}
                value={progress?.failed || 0}
                styles={{ content: { color: progress?.failed > 0 ? '#ff4d4f' : undefined } }}
                prefix={progress?.failed > 0 ? <CloseCircleOutlined /> : null}
              />
            </Col>
          </Row>
        }
      />
    );
  }

  if (importStatus === 'failed') {
    return (
      <Result
        status="error"
        title={translateX('data_import_failed')}
        subTitle={progress?.errors?.[0]?.message || 'An error occurred during import'}
      />
    );
  }

  // Processing in progress
  return (
    <Flex vertical align="center" gap={24} style={{ padding: '24px 0' }}>
      <Title level={5}>{translateX('data_import_processing_description')}</Title>

      <Progress
        type="circle"
        percent={progressPercent}
        size={120}
        strokeColor={{ '0%': '#1677ff', '100%': '#52c41a' }}
      />

      <Row gutter={[24, 12]} style={{ width: '100%', maxWidth: 400 }}>
        <Col span={12}>
          <Statistic
            title={translateX('processing')}
            value={progress?.processed || 0}
            suffix={`/ ${progress?.total || 0}`}
            styles={{ content: { fontSize: 18 } }}
          />
        </Col>
        <Col span={12}>
          <Statistic
            title={translateX('data_import_imported_records')}
            value={progress?.imported || 0}
            styles={{ content: { fontSize: 18, color: '#52c41a' } }}
          />
        </Col>
        <Col span={12}>
          <Statistic
            title={translateX('data_import_skipped_records')}
            value={progress?.skipped || 0}
            styles={{ content: { fontSize: 18 } }}
          />
        </Col>
        <Col span={12}>
          <Statistic
            title={translateX('data_import_failed_records')}
            value={progress?.failed || 0}
            styles={{ content: { fontSize: 18, color: progress?.failed > 0 ? '#ff4d4f' : undefined } }}
          />
        </Col>
      </Row>
    </Flex>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const stepProcessing = ({ progress, importStatus, startExecution, hasStartedExecutionRef }) => {
  const progressPercent = progress?.total > 0
    ? Math.round((progress.processed / progress.total) * 100)
    : 0;

  return {
    stepTitle: 'processing',
    stepTooltip: 'data_import_processing_description',
    content: () => (
      <StepProcessingContent
        progress={progress}
        importStatus={importStatus}
        progressPercent={progressPercent}
        startExecution={startExecution}
        hasStartedExecutionRef={hasStartedExecutionRef}
      />
    ),
    nextButtonText: importStatus === 'completed' || importStatus === 'failed' ? 'close' : 'processing',
    nextButtonIcon: importStatus === 'completed' ? <CheckCircleOutlined /> : null,
  };
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default stepProcessing;
